import React from "react";

import styles from "./Select.module.css";

import closeIcon from "../icons/close.svg";
import arrowIcon from "../icons/arrow.svg";
import arrowChevronIcon from "../icons/arrow-chevron.svg";
import blueArrow from "./blue-arrow.svg";

import DropDown from "../drop-down/DropDown";
import { generateUUID } from "../utils/string-utils";
import Button from "../button/Button";
import MultiSelectTextInput from "../input/multi-select-text-input/MultiSelectTextInput";
import whiteArrow from "./whiteArrow.svg";

export const IconVariants = {
  arrow: arrowIcon,
  chevron: arrowChevronIcon,
  whiteArrow,
  blueArrow,
};

export const TriggerComponentVariants = {
  default: "default",
  searchable: "searchable",
  searchableInline: "searchableInline",
};

const getOptionText = (option) => {
  if (option === null || option === undefined) return "";
  if (typeof option === "object")
    return (
      option.label ?? option.value ?? option.name ?? JSON.stringify(option)
    );
  return String(option);
};

const SearchableSelectTrigger = ({
  active,
  makeSelectedListVertical,
  triggerText,
  inlineInput,
  updateOptionFilterText,
  options,
  searchWidth,
  single,
  handleAddOption,
  handleRemoveOption,
  ...rest
}) => {
  const handleUpdateSearch = (text) => {
    if (rest && typeof rest.onSearchTextChange === "function") {
      rest.onSearchTextChange(text);
    }
    if (typeof updateOptionFilterText === "function") {
      updateOptionFilterText(text);
    }
    if (rest && typeof rest.setOpen === "function") {
      if (text != null && String(text).trim().length > 0) {
        rest.setOpen(true);
      }
    }
  };

  const activeArray = Array.isArray(active)
    ? active
    : active !== null && active !== undefined
      ? [active]
      : [];

  return (
    <MultiSelectTextInput
      textArr={activeArray.map((ele) => getOptionText(options[ele]))}
      placeHolderText={triggerText}
      textType={"triggerComponent"}
      handleRemoveText={handleRemoveOption}
      handleAddText={handleAddOption}
      needRemoveButton={rest.showRemoveIcon !== false}
      makeSelectedListVertical={makeSelectedListVertical}
      updateSearchText={handleUpdateSearch}
      inlineInput={inlineInput}
      searchWidth={searchWidth}
      single={single}
      {...rest}
    />
  );
};

const CustomSelectTrigger = ({
  optionFilterText,
  updateOptionFilterText,
  activeColor,
  triggerComponentType,
  maxVisibleSelections,
  makeSelectedListVertical,
  ...rest
}) => {
  if (triggerComponentType === TriggerComponentVariants.searchable) {
    return (
      <SearchableSelectTrigger
        {...rest}
        inlineInput={false}
        updateOptionFilterText={updateOptionFilterText}
        makeSelectedListVertical={makeSelectedListVertical} // FIX 2: Restored
      />
    );
  } else if (
    triggerComponentType === TriggerComponentVariants.searchableInline
  ) {
    return (
      <SearchableSelectTrigger
        {...rest}
        inlineInput={true}
        updateOptionFilterText={updateOptionFilterText}
        makeSelectedListVertical={makeSelectedListVertical} // FIX 2: Restored
      />
    );
  }

  return (
    <SelectTrigger
      maxVisibleSelections={maxVisibleSelections}
      activeColor={activeColor}
      makeSelectedListVertical={makeSelectedListVertical}
      {...rest}
    />
  );
};

const Select = ({
  options,
  textColors,
  multiple,
  active = multiple ? [] : 0,
  setSelected,
  setActive,
  onOptionSelect,
  onOptionHover,
  onOptionHoverOut,
  containerProps,
  triggerText,
  triggerComponent,
  triggerComponentType = TriggerComponentVariants.default,
  triggerProps,
  handleAddOption,
  handleRemoveOption,
  onClose,
  iconProps,
  children,
  makeSelectedListVertical = false,
  maxVisibleSelections = 6,
  childrenProps,
  ExtraComponent = null,
  searchWidth = null,
  single = false,
  noResultsText = "No records found",
  ...rest
}) => {
  const activeColor =
    !multiple && typeof active === "number" && Array.isArray(textColors)
      ? textColors[active]
      : null;

  return (
    <div style={{ display: "flex", gap: "0.4rem" }}>
      <DropDown
        containerProps={containerProps}
        triggerComponent={triggerComponent || CustomSelectTrigger}
        triggerProps={{
          triggerText,
          activeElement: options[active],
          options,
          textColors,
          activeColor,
          active,
          makeSelectedListVertical,
          setActive,
          multiple,
          triggerComponentType,
          maxVisibleSelections,
          searchWidth,
          single,
          handleAddOption,
          handleRemoveOption,
          ...triggerProps,
          iconProps,
          ...rest,
        }}
        childrenProps={{
          options,
          textColors,
          active,
          multiple,
          onOptionSelect,
          onOptionHover,
          onOptionHoverOut,
          noResultsText,
          ...childrenProps,
        }}
        triggerComponentType={triggerComponentType}
        onClose={onClose}
        ExtraComponent={ExtraComponent}
      >
        {children || SelectContent}
      </DropDown>
    </div>
  );
};

const SelectTrigger = ({
  open,
  setOpen,
  activeElement,
  triggerText,
  toolTipText,
  triggerTextStyle,
  arrowContainerStyle,
  iconProps,
  multiple,
  options,
  activeColor,
  active,
  setActive,
  makeSelectedListVertical,
  maxVisibleSelections,
  showRemoveIcon = true,
  ...rest
}) => {
  const triggerTextInternal = triggerText || getOptionText(activeElement);
  const normalizedTooltipText = toolTipText?.length > 0 ? toolTipText : null;
  return (
    <div
      {...rest}
      {...(normalizedTooltipText
        ? { "data-title": normalizedTooltipText }
        : {})}
      className={[styles.container, rest?.className].join(" ")}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.8rem",
        ...rest?.style,
        ...(open && { borderColor: "#0070D2" }),
      }}
    >
      {(!multiple || (multiple && !active.length)) && (
        <span
          style={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ...triggerTextStyle,
            ...(activeColor !== null && { color: activeColor }),
          }}
        >
          {triggerTextInternal}
        </span>
      )}
      {multiple && (
        <div
          className={styles.selectedOptions}
          style={{
            gridTemplateColumns: makeSelectedListVertical
              ? "repeat(1, 1fr)"
              : "repeat(3, 1fr)",
          }}
        >
          {active.slice(0, maxVisibleSelections).map((ele, _ind) => (
            <SelectedOption
              key={ele}
              option={options[ele]}
              index={ele}
              setActive={setActive}
              showRemoveIcon={showRemoveIcon}
            />
          ))}
        </div>
      )}
      <div className={styles.arrowContainer} style={arrowContainerStyle}>
        {/* asd */}
        <img
          src={(iconProps && IconVariants[iconProps.iconVariant]) || arrowIcon}
          className={[styles.icon, open ? styles.show : ""].join(" ")}
          style={iconProps?.style}
          width={iconProps?.size || 16}
          height={iconProps?.size || 16}
          alt="select"
        />
        {multiple && active.length > maxVisibleSelections && (
          <Button
            type="button"
            style={{ fontWeight: "bold", border: "none", color: "#2f7ee9" }}
          >
            +{active.length - maxVisibleSelections} more
          </Button>
        )}
      </div>
    </div>
  );
};

const SelectedOption = ({
  option,
  index,
  setActive,
  showRemoveIcon = true,
}) => {
  const handleRemove = (e) => {
    e.stopPropagation();
    setActive((prevActive) =>
      prevActive.filter((value, _ind) => value !== index),
    );
  };
  return (
    <div className={styles.selectedOption}>
      <span className={styles.selectedOptionTitle}>{option}</span>
      {showRemoveIcon && (
        <img
          className={styles.closeIcon}
          src={closeIcon}
          alt="discard"
          width={11}
          height={11}
          onClick={handleRemove}
        />
      )}
    </div>
  );
};

const SelectContent = ({ ...rest }) => {
  if (rest.multiple) return <MultipleOptions {...rest} />;
  return <SelectOptions {...rest} />;
};

/**
 * Multiple Options Component
 * @param {Boolean} open - Whether the dropdown is open
 * @param {Function} setOpen - Function to set the dropdown open state
 * open and setOpen will be passed down from the DropDown component or select component if used as their children
 * @param {Array} options - Array of options to display
 * @param {Number[]} active - Array of active indexes
 * @param {Object} multiple - Object containing multiple options
 * @param {Function} onOptionSelect - Function to handle option selection
 * @returns {JSX.Element}
 *
 * @example
 * <MultipleOptions
 *  open={open}
 *  setOpen={setOpen}
 *  options={options}
 *  active={active}
 *  multiple={{
 *   handleReset: () => setActive([]),
 *   handleApply: () => //console.log("Applied"),
 *   optionsText: "Options",
 *   resetText: "Reset",
 *   applyText: "Apply",
 *  }}
 *  onOptionSelect={(option, index) => setActive([index])}
 * />
 */
const MultipleOptions = ({
  open,
  setOpen,
  options,
  textColors,
  active,
  multiple,
  onOptionSelect,
  ...rest
}) => {
  if (typeof multiple !== "object")
    throw new Error("Multiple prop should be an object");

  const onResetClick = () => {
    multiple.handleReset();
    setOpen(false);
  };

  const onApplyClick = () => {
    multiple.handleApply();
    setOpen(false);
  };

  return (
    <div className={styles.multipleOptions}>
      <div className={styles.multipleOptionsList}>
        <SelectOptions
          {...rest}
          {...{
            open,
            setOpen,
            options,
            textColors,
            active,
            multiple,
            onOptionSelect,
          }}
        />
      </div>
      <div className={styles.multipleOptionsControls}>
        <span>
          {active.length} {multiple.optionsText || "Options"} chosen
        </span>
        <div className={styles.multipleOptionsButtons}>
          <button
            type="button"
            onClick={onResetClick}
            className={styles.resetBtn}
          >
            {multiple.resetText || "Reset"}
          </button>
          {multiple.hideApplyButton ? null : (
            <button
              type="button"
              onClick={onApplyClick}
              className={styles.applyBtn}
            >
              {multiple.applyText || "Apply"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const SelectOptions = ({
  open,
  setOpen,
  multiple,
  options,
  textColors,
  onOptionSelect,
  onOptionHover,
  onOptionHoverOut,
  active,
  optionFilterText,
  noResultsText = "No records found",
  ...rest
}) => {
  const handleOptionClick = (option, index) => {
    if (onOptionSelect) onOptionSelect(option, index);
    if (!multiple && setOpen) setOpen(false);
  };

  const handleOptionHover = (option, index) => {
    if (onOptionHover) onOptionHover(option, index);
  };

  const handleOptionHoverOut = (option, index) => {
    if (onOptionHoverOut) onOptionHoverOut(option, index);
  };

  const filterText = optionFilterText
    ? String(optionFilterText).toLowerCase().trim()
    : "";
  const hasFilter = filterText.length > 0;

  // Build list of visible indexes so we preserve original indexes (important for multi-select)
  const visibleIndexes = options
    .map((opt, idx) => {
      if (opt === null || opt === undefined) return null; // skip null/undefined
      const optionText = getOptionText(opt).toLowerCase();
      if (!hasFilter) return idx;
      return optionText.includes(filterText) ? idx : null;
    })
    .filter((idx) => idx !== null);

  // If user searched and nothing matched -> show noResultsText
  if (hasFilter && visibleIndexes.length === 0) {
    return (
      <ul
        {...rest}
        className={[styles.selectOptions, rest?.className].join(" ")}
      >
        <li
          className={styles.noResults}
          role="status"
          aria-live="polite"
          style={{ padding: "0.5rem 1rem", cursor: "default" }}
        >
          {noResultsText}
        </li>
      </ul>
    );
  }

  // If there are no options at all -> show noResultsText
  if (!options || options.length === 0) {
    return (
      <ul
        {...rest}
        className={[styles.selectOptions, rest?.className].join(" ")}
      >
        <li
          className={styles.noResults}
          role="status"
          aria-live="polite"
          style={{ padding: "0.5rem 1rem", cursor: "default" }}
        >
          {noResultsText}
        </li>
      </ul>
    );
  }

  // Otherwise render visible options in original order
  return (
    <ul {...rest} className={[styles.selectOptions, rest?.className].join(" ")}>
      {visibleIndexes.map((index) => {
        const option = options[index];
        const id = generateUUID();
        return (
          <SelectOption
            key={id}
            id={id}
            handleOptionClick={handleOptionClick}
            handleOptionHover={handleOptionHover}
            handleOptionHoverOut={handleOptionHoverOut}
            option={option}
            textColor={textColors ? textColors[index] : null}
            active={active}
            index={index}
            multiple={multiple}
          />
        );
      })}
    </ul>
  );
};

export default Select;

export const SelectOption = ({
  id,
  handleOptionClick,
  handleOptionHover,
  handleOptionHoverOut,
  option,
  textColor,
  active,
  index,
  multiple,
  ...rest
}) => {
  const derivedActive = !multiple ? active === index : active.includes(index);

  return (
    <li style={{ display: "contents" }}>
      <button
        style={{ display: "contents" }}
        onClick={() => handleOptionClick(option, index)}
      >
        <label
          htmlFor={id}
          {...rest}
          onMouseEnter={() => handleOptionHover(option, index)}
          onMouseLeave={() => handleOptionHoverOut(option, index)}
          className={[
            styles.selectOption,
            active === index && styles.active,
            rest?.className,
          ].join(" ")}
        >
          <input
            type={`${multiple ? "checkbox" : "radio"}`}
            id={id}
            hidden={!multiple}
            name="select-option"
            {...(multiple
              ? {
                  checked: derivedActive,
                  onChange: () => {},
                }
              : {})}
          />
          {option?.icon && (
            <img
              src={option.icon}
              alt={option?.label ?? option?.value ?? "option-icon"}
              width={16}
              height={16}
              style={{ marginRight: "0.5rem" }}
            />
          )}
          <span style={textColor ? { color: textColor } : null}>
            {getOptionText(option)}
          </span>
        </label>
      </button>
    </li>
  );
};
